import * as Sentry from '@sentry/nextjs';

// Generate unique correlation IDs for request tracing
export function generateCorrelationId(): string {
  return `req_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;
}

// Generate job correlation IDs for background tasks
export function generateJobCorrelationId(jobId?: string): string {
  const baseId = `job_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;
  return jobId ? `${baseId}_${jobId}` : baseId;
}

/**
 * Keys whose values must never reach a persistent sink (Sentry). Matched
 * case-insensitively as substrings of the normalized key, so `accessToken`,
 * `refreshToken`, `authorizationHeader`, and `userEmail` are all caught.
 * Over-redaction of benign token-ish keys (e.g. `tokenId`) is the accepted
 * safe default.
 */
const SENSITIVE_KEY_PATTERNS = [
  'password',
  'passwd',
  'secret',
  'token',
  'authorization',
  'cookie',
  'credential',
  'apikey',
  'email',
  'ssn',
  'body',
  'payload',
] as const;

/**
 * Returns a shallow copy of `context` with sensitive values replaced by
 * '[REDACTED]'. Never throws — logging must not crash its caller. This is the
 * class-wide guardrail (AUD2-021): a caller that accidentally puts a token,
 * secret, or PII into logger context no longer forwards it to Sentry.
 */
export function redactSensitiveContext(
  context?: Record<string, unknown>
): Record<string, unknown> | undefined {
  if (!context) return context;
  const redacted: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(context)) {
    const normalized = key.toLowerCase().replace(/[^a-z0-9]/g, '');
    redacted[key] = SENSITIVE_KEY_PATTERNS.some(pattern =>
      normalized.includes(pattern)
    )
      ? '[REDACTED]'
      : value;
  }
  return redacted;
}

// Core AppLogger class with structured logging
export class AppLogger {
  static info(logMessage: string, context?: Record<string, unknown>) {
    const timestamp = new Date().toISOString();

    // Development: rich console output with emojis
    if (process.env.NODE_ENV === 'development') {
      console.log(`ℹ️ [${timestamp}] ${logMessage}`, context || '');
    }

    // Production: Sentry structured logging
    if (process.env.NODE_ENV === 'production') {
      const { logger } = Sentry;
      logger.info(logMessage, redactSensitiveContext(context));
    }
  }

  static error(
    logMessage: string,
    error?: Error,
    context?: Record<string, unknown>
  ) {
    const timestamp = new Date().toISOString();

    // Development: rich console output with emojis
    if (process.env.NODE_ENV === 'development') {
      console.error(
        `❌ [${timestamp}] ${logMessage}`,
        error || '',
        context || ''
      );
    }

    // Production: structured logging only (no Sentry capture)
    // Note: Sentry error tracking is handled by mutation/action wrappers
    if (process.env.NODE_ENV === 'production') {
      const { logger } = Sentry;
      logger.error(logMessage, {
        ...redactSensitiveContext(context),
        error: error?.message,
      });
    }
  }

  static warn(logMessage: string, context?: Record<string, unknown>) {
    const timestamp = new Date().toISOString();

    // Development: rich console output with emojis
    if (process.env.NODE_ENV === 'development') {
      console.warn(`⚠️ [${timestamp}] ${logMessage}`, context || '');
    }

    // Production: Sentry structured logging
    if (process.env.NODE_ENV === 'production') {
      const { logger } = Sentry;
      logger.warn(logMessage, redactSensitiveContext(context));
    }
  }

  static debug(logMessage: string, context?: Record<string, unknown>) {
    // Only log in development when DEBUG is enabled
    if (
      process.env.NODE_ENV === 'development' &&
      process.env.DEBUG === 'true'
    ) {
      const timestamp = new Date().toISOString();
      console.log(`🔍 [${timestamp}] ${logMessage}`, context || '');
    }
  }

  static trace(logMessage: string, context?: Record<string, unknown>) {
    const timestamp = new Date().toISOString();

    // Development: rich console output with emojis
    if (process.env.NODE_ENV === 'development') {
      console.log(`🔍 [${timestamp}] ${logMessage}`, context || '');
    }

    // Production: Sentry structured logging
    if (process.env.NODE_ENV === 'production') {
      const { logger } = Sentry;
      logger.trace(logMessage, redactSensitiveContext(context));
    }
  }

  static fatal(
    logMessage: string,
    error?: Error,
    context?: Record<string, unknown>
  ) {
    const timestamp = new Date().toISOString();

    // Development: rich console output with emojis
    if (process.env.NODE_ENV === 'development') {
      console.error(
        `💀 [${timestamp}] ${logMessage}`,
        error || '',
        context || ''
      );
    }

    // Production: structured logging only (no Sentry capture)
    // Note: Sentry error tracking is handled by mutation/action wrappers
    if (process.env.NODE_ENV === 'production') {
      const { logger } = Sentry;
      logger.fatal(logMessage, {
        ...redactSensitiveContext(context),
        error: error?.message,
      });
    }
  }
}

// Generic breadcrumb validation for any provider
export function validateBreadcrumbFields(
  context: Record<string, unknown>,
  whitelist: readonly string[],
  provider: string
): void {
  const contextKeys = Object.keys(context);

  // First check for sensitive data (higher priority)
  const sensitiveFields = [
    'text',
    'message',
    'payload',
    'body',
    'content',
    'password',
    'token',
    'secret',
  ];
  const hasSensitiveData = contextKeys.some(key =>
    sensitiveFields.includes(key)
  );

  if (hasSensitiveData) {
    throw new Error(
      `${provider} breadcrumbs must not contain sensitive data: ${sensitiveFields.join(', ')}`
    );
  }

  // Then validate that only whitelisted fields are present
  const invalidFields = contextKeys.filter(key => !whitelist.includes(key));

  if (invalidFields.length > 0) {
    throw new Error(
      `Invalid breadcrumb fields for ${provider}: ${invalidFields.join(', ')}. Only whitelisted fields allowed: ${whitelist.join(', ')}`
    );
  }
}
